import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { GoogleSignIn } from "@/pages/auth/GoogleSignIn";
import { useAuth } from "@/context/AuthContext";
import { homeFor } from "@/lib/roles";
import { Shell } from "@/pages/Auth";
import { TwoFactor } from "@/pages/auth/TwoFactor";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Baby, CheckCircle, WarningCircle } from "@phosphor-icons/react";

const strong = (p) => p.length >= 10 && /[a-z]/.test(p) && /[A-Z]/.test(p) && /\d/.test(p);

export default function Invite() {
  const { token } = useParams();
  const location = useLocation();
  const nav = useNavigate();
  const { user, login, setSession, reload } = useAuth();
  const processed = useRef(false);
  const [inv, setInv] = useState(null);
  const [error, setError] = useState("");
  const [agree, setAgree] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", password: "", confirm: "" });
  const [creds, setCreds] = useState({ email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [mfa, setMfa] = useState(null);
  const [done, setDone] = useState(null);

  useEffect(() => {
    api.get(`/auth/invitations/${token}`)
      .then(({ data }) => { setInv(data); setForm((f) => ({ ...f, name: data.name || "", phone: data.phone || "" })); })
      .catch((e) => setError(apiError(e.response?.data?.detail)));
  }, [token]);

  useEffect(() => {
    // Google sends the user back here with #session_id=… — exchange it once, together with the invitation.
    const m = /session_id=([^&]+)/.exec(location.hash || "");
    if (!m || processed.current) return;
    processed.current = true;
    window.history.replaceState(null, "", location.pathname);
    api.post("/auth/google/session", { session_id: decodeURIComponent(m[1]), invite_token: token, agree_terms: true })
      .then(({ data }) => (data.mfa_required ? setMfa(data) : finish(data)))
      .catch((e) => setMsg(apiError(e.response?.data?.detail)));
  }, [location, token]); // eslint-disable-line react-hooks/exhaustive-deps

  const finish = (u) => { setSession(u); setDone(u); };

  const createAccount = async (e) => {
    e.preventDefault();
    if (!strong(form.password)) return setMsg("Password must be at least 10 characters with an uppercase letter, a lowercase letter and a number.");
    if (form.password !== form.confirm) return setMsg("The two passwords do not match.");
    setBusy(true); setMsg("");
    try {
      const { data } = await api.post(`/auth/invitations/${token}/accept`, { name: form.name, phone: form.phone, password: form.password, agree_terms: agree });
      if (data.mfa_required) setMfa(data); else finish(data);
    } catch (err) { setMsg(apiError(err.response?.data?.detail)); }
    setBusy(false);
  };

  const acceptExisting = async () => {
    setBusy(true); setMsg("");
    try {
      const { data } = await api.post(`/auth/invitations/${token}/accept-existing`);
      finish(data); await reload();
    } catch (err) { setMsg(apiError(err.response?.data?.detail)); }
    setBusy(false);
  };

  const signInThenAccept = async (e) => {
    e.preventDefault();
    setBusy(true); setMsg("");
    const r = await login(creds.email, creds.password);
    setBusy(false);
    if (r.mfa) setMfa({ ...r.mfa, thenAccept: true });
    else if (!r.ok) setMsg(r.error);
  };

  useEffect(() => {
    if (user && inv?.existing_account && !done && !mfa && user.email === inv.email) acceptExisting();
  }, [user, inv]); // eslint-disable-line react-hooks/exhaustive-deps

  if (error)
    return (
      <Shell title="Invitation unavailable" sub="">
        <div className="mt-8 bg-white rounded-3xl border border-border p-6" data-testid="invite-error">
          <WarningCircle size={40} weight="fill" className="text-[#FBBF24]" />
          <p className="mt-4 font-semibold">{error}</p>
          <p className="mt-3 text-sm text-muted-foreground">Call (501) 904-7103 and we'll send a fresh invitation.</p>
          <Link to="/login"><Button variant="outline" className="mt-6 w-full h-12 rounded-full font-bold" data-testid="invite-error-login">Go to sign in</Button></Link>
        </div>
      </Shell>
    );

  if (done)
    return (
      <Shell title="You're all set!" sub="">
        <div className="mt-8 bg-white rounded-3xl border border-border p-6 text-center" data-testid="invite-success">
          <CheckCircle size={64} weight="fill" className="mx-auto text-[#10B981]" />
          <p className="mt-4 font-semibold">Your account is connected{inv?.children?.length ? ` to ${inv.children.map((c) => c.name).join(", ")}` : ""}.</p>
          <Button onClick={() => nav(homeFor(done.role))} className="mt-6 w-full h-12 rounded-full bg-[#3B82F6] hover:bg-[#2563EB] font-bold" data-testid="invite-go-portal">
            {done.role === "parent" ? "Open my family portal" : "Open the staff portal"}
          </Button>
        </div>
      </Shell>
    );

  if (mfa)
    return (
      <Shell title="Check your email" sub="One more step keeps your family's information safe.">
        <TwoFactor mfa={mfa} onSuccess={(u) => { setSession(u); if (mfa.thenAccept) { setMfa(null); } else finish(u); }} />
      </Shell>
    );

  if (!inv) return <div className="min-h-screen grid place-items-center text-muted-foreground" data-testid="invite-loading">Loading your invitation…</div>;

  const isParent = inv.role === "parent";
  return (
    <Shell title={isParent ? "Welcome to the family portal" : "Join the Teach Play to Learn team"}
           sub={`${inv.invited_by || "The office"} invited ${inv.email}.`}>
      <div className="mt-6 bg-white rounded-3xl border border-border p-6" data-testid="invite-card">
        {isParent ? (
          <>
            <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#3B82F6]">Your children</p>
            <div className="mt-3 space-y-2" data-testid="invite-children">
              {inv.children.map((c) => (
                <div key={c.name} className="flex items-center gap-3 rounded-2xl bg-blue-50 px-4 py-3">
                  <Baby size={22} weight="duotone" className="text-[#3B82F6]" />
                  <span className="font-bold">{c.name}</span><span className="text-sm text-muted-foreground">{c.room}</span>
                </div>
              ))}
            </div>
            <p className="mt-4 text-sm text-muted-foreground">Relationship: <strong>{inv.relationship}</strong> · Access: <strong>{inv.permission_level}</strong></p>
          </>
        ) : (
          <p className="text-sm">You've been invited as <strong>{inv.role_label}</strong>.</p>
        )}
        <p className="mt-3 text-xs text-muted-foreground">This link works once and expires {new Date(inv.expires_at).toLocaleDateString()}.</p>
      </div>

      {msg && <p className="mt-4 text-sm font-semibold text-[#EF4444] bg-red-50 rounded-2xl p-4" data-testid="invite-message">{msg}</p>}

      {inv.existing_account ? (
        <div className="mt-6" data-testid="invite-existing">
          <p className="text-sm bg-amber-50 text-amber-800 rounded-2xl p-4 font-semibold">
            An account already exists for {inv.email}{inv.existing_role_label ? ` (${inv.existing_role_label})` : ""}. Sign in to accept
            this invitation — your current access stays exactly as it is.
          </p>
          {user && user.email === inv.email ? (
            <Button onClick={acceptExisting} disabled={busy} className="mt-4 w-full h-12 rounded-full bg-[#3B82F6] hover:bg-[#2563EB] font-bold" data-testid="invite-accept-existing">
              {busy ? "Connecting…" : "Accept and connect"}
            </Button>
          ) : user ? (
            <p className="mt-4 text-sm font-semibold text-[#EF4444]" data-testid="invite-wrong-user">
              You're signed in as {user.email}. Please <Link to="/login" className="underline">sign out</Link> and use {inv.email}.
            </p>
          ) : (
            <form onSubmit={signInThenAccept} className="mt-4 space-y-4" data-testid="invite-login-form">
              <GoogleSignIn onSession={finish} onMfa={setMfa} inviteToken={token}
                            onError={setMsg} testid="invite-google-existing" />
              <div className="flex items-center gap-3 text-xs font-bold uppercase tracking-[0.18em] text-muted-foreground">
                <span className="h-px flex-1 bg-border" /> or with your password <span className="h-px flex-1 bg-border" />
              </div>
              <Input type="email" required value={creds.email} onChange={(e) => setCreds({ ...creds, email: e.target.value })} placeholder="Email" className="h-12 rounded-xl" data-testid="invite-login-email" />
              <Input type="password" required value={creds.password} onChange={(e) => setCreds({ ...creds, password: e.target.value })} placeholder="Password" className="h-12 rounded-xl" data-testid="invite-login-password" />
              <Button type="submit" disabled={busy} className="w-full h-12 rounded-full bg-[#3B82F6] hover:bg-[#2563EB] font-bold" data-testid="invite-login-submit">Sign in to accept</Button>
            </form>
          )}
        </div>
      ) : (
        <div className="mt-6">
          <label className="flex items-start gap-3 text-sm rounded-2xl border border-border p-4 bg-white" data-testid="invite-terms">
            <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} className="mt-1 h-5 w-5" data-testid="invite-terms-checkbox" />
            <span><strong>I accept the daycare terms.</strong> {inv.terms}</span>
          </label>
          <div className="mt-5">
            <GoogleSignIn onSession={finish} onMfa={setMfa} inviteToken={token} agreeTerms={agree}
                          disabled={!agree} onError={setMsg} text="signup_with" testid="invite-google-btn" />
            <div className="my-5 flex items-center gap-3 text-xs font-bold uppercase tracking-[0.18em] text-muted-foreground">
              <span className="h-px flex-1 bg-border" /> or create a password <span className="h-px flex-1 bg-border" />
            </div>
          </div>
          <form onSubmit={createAccount} className="space-y-4" data-testid="invite-password-form">
            <div><Label className="text-sm font-bold">Your full name</Label>
              <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-2 h-12 rounded-xl" data-testid="invite-name" /></div>
            <div><Label className="text-sm font-bold">Email</Label>
              <Input value={inv.email} disabled className="mt-2 h-12 rounded-xl bg-muted" data-testid="invite-email" /></div>
            <div><Label className="text-sm font-bold">Phone (optional)</Label>
              <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-2 h-12 rounded-xl" data-testid="invite-phone" /></div>
            <div><Label className="text-sm font-bold">Create a password</Label>
              <Input type="password" required autoComplete="new-password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-2 h-12 rounded-xl" data-testid="invite-password" />
              <p className={`mt-2 text-xs ${form.password && !strong(form.password) ? "text-[#EF4444] font-semibold" : "text-muted-foreground"}`}>At least 10 characters with an uppercase letter, a lowercase letter and a number.</p></div>
            <div><Label className="text-sm font-bold">Confirm password</Label>
              <Input type="password" required autoComplete="new-password" value={form.confirm} onChange={(e) => setForm({ ...form, confirm: e.target.value })} className="mt-2 h-12 rounded-xl" data-testid="invite-confirm" /></div>
            <Button type="submit" disabled={busy || !agree} className="w-full h-12 rounded-full bg-[#FBBF24] hover:bg-[#F59E0B] text-[#0F172A] font-bold" data-testid="invite-create-btn">
              {busy ? "Creating…" : "Create my account"}
            </Button>
          </form>
        </div>
      )}
    </Shell>
  );
}
